import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import ErrorIcon from '../../../icons/error.svg';

export type OpenParametersSegmentTriggerProps = {
	icon: React.ReactNode;
	label: string;
	hasValue: boolean;
	isOpen: boolean;
	hasError: boolean;
	onClear: () => void;
} & Omit<React.ComponentPropsWithoutRef<'button'>, 'type'>;

export const OpenParametersSegmentTrigger = React.forwardRef<
	HTMLButtonElement,
	OpenParametersSegmentTriggerProps
>(function OpenParametersSegmentTrigger(
	{ icon, label, hasValue, isOpen, hasError, onClear, className, ...props },
	ref
) {
	const { t } = useTranslation();

	const handleClearKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			onClear();
		}
	};

	return (
		<button
			ref={ref}
			type="button"
			className={cn(
				'flex w-full min-h-[60px] max-h-[60px] shrink-0 self-center items-center justify-between rounded-[32px] pl-6 pr-3 text-left',
				isOpen && 'bg-white/10',
				className
			)}
			{...props}
		>
			<span className="flex min-w-0 items-center gap-1.5">
				{icon}
				<span
					className={cn(
						'whitespace-nowrap font-poppins text-lg font-medium leading-none',
						hasValue ? 'text-white' : 'text-white/70'
					)}
				>
					{label}
				</span>
			</span>
			{hasValue && isOpen ? (
				<span
					role="button"
					tabIndex={0}
					onClick={e => {
						e.preventDefault();
						e.stopPropagation();
						onClear();
					}}
					onKeyDown={e => {
						e.stopPropagation();
						handleClearKeyDown(e);
					}}
					className="flex size-[18px] shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white"
				>
					<X aria-hidden strokeWidth={2.5} className="size-3" />
				</span>
			) : hasError ? (
				<img src={ErrorIcon} alt={t('aria.error_icon', { defaultValue: 'Error icon' })} className="size-[18px] shrink-0" />
			) : (
				<span className="size-[18px] shrink-0" aria-hidden />
			)}
		</button>
	);
});

OpenParametersSegmentTrigger.displayName = 'OpenParametersSegmentTrigger';
